import { $t, LocalContext } from './i18n/LocalContext'
import Home from '@app/pages/Home/Home'
import { StateContext, DispatchContext, useDispatch } from './store'
import { StrictMode, useEffect, useRef, useState } from 'react'
import Toast from './components/Toast/Toast'
import Pending from './components/Pending/Pending'
import useUserInfo from './hooks/useUserInfo'
import { closeLoading, loading, throwError, toast } from './utils'
// import Cookies from 'js-cookie'
import {
  setToken,
  loginMessageIsString,
  clearToken,
  getToken,
  logout,
} from './request/utils'
import { Handler } from 'mitt'
import SelectRole from './components/SelectRole/SelectRole'
import Alert from './components/Alert/Alert'
import appExpose from '@app/expose'
import enLocalJson from '@app/i18n/index-langs/en.json'
import { checkToken, getBindedPlayer, getRoleList } from './request/userServer'

const { config, emitter } = appExpose
const inSDK = window.inSDK

function Root() {
  const { lang, gamePlayer, sdkConfig, userInfo } = useUserInfo()
  const { state, dispatch } = useDispatch()
  const userId = state.login.userInfo.userId
  const [local, setLocal] = useState<Record<string, string> | null>(null)
  const [ready, setReady] = useState(false)

  const changeLoginStatus = (data: { logined: boolean; token: string }) => {
    dispatch({
      type: 'login',
      data,
    })
  }

  // 登录
  useEffect(() => {
    const _cancel = () => {
      if (window.loginApp) {
        window.loginApp.destroy()
        window.loginApp = null
      }
    }

    if (lang && local == null) {
      try {
        import(`@app/i18n/index-langs/${lang}.json`)
          .then((m) => m.default)
          .then((localJson) => {
            window.local = localJson
            setLocal(localJson)
          })
      } catch (_) {
        window.local = enLocalJson
        setLocal(enLocalJson)
      }
      return _cancel
    }

    if (lang && (!inSDK || (inSDK && !userId))) {
      console.log('自動登錄')
      const token = getToken()
      if (token) {
        window.checkToken$ = checkToken().then(() => {
          window.getBindedPlayer$ = getBindedPlayer()
        })
      }
      dispatch({
        type: 'setLoginData',
        data: {
          lang,
          logined: !!token,
          gamePlayer,
          sdkConfig,
          userInfo,
        },
      })
    }

    if (lang && (!inSDK || (inSDK && userId))) {
      console.log('lang =>', lang)
      console.log('userId =>', userId)

      if (window.onemtLogin) {
        const preKey = 'kc'
        window.loginApp = new window.onemtLogin({
          el: '.web-login-container',
          game: 'kow',
          preKey,
          lang,
          area: window.queryObj.area, //
          autoSelect: false,
          colors: {
            background: '#49565a',
            title: '#ffe9b9',
            close: '#ffefcb',
            text: '#ffefcb',
            primary: '#f4cc71',
            cancel: '#efede3',
            cancelText: '#33333399',
            buttonText: '#33333399',
            selectColor: '#fff',
          },
          termsUrl: 'https://pte.onemt.com/terms',
          policyUrl: 'https://pte.onemt.com/policy',
          multiRole: true,
          confirm: (data) => {
            if (data.type === 'confirm') {
              if (data.status === 'openRoles') {
                logout()
              }
            }
          },
          getLoginInfo(res) {
            // if (loginMessageIsString(res)) return
            try {
              setToken(res)
              changeLoginStatus({
                logined: true,
                token: getToken(),
              })
            } catch (error) {
              throwError(error)
            }
            window.checkToken$ = checkToken()
          },
          userToken: async () => {
            loading()
            const res = await window.checkToken$
              .then(() => {
                window.getBindedPlayer$ = getBindedPlayer()
                return true
              })
              .catch((err) => err)
            closeLoading()
            return res
          },
          // @ts-ignore
          getRoleList: async () => {
            const bindedPlayer = await window.getBindedPlayer$
            const zoneId = bindedPlayer.zoneId
            const isEur = [1, 2].includes(zoneId)
            const _getAllRoleList = () => {
              if (!isEur) {
                const list = getRoleList(zoneId, userId).then(
                  (item) => [item[0], item[1]] as const
                )
                return list
              }

              return Promise.all([
                getRoleList(1, userId),
                getRoleList(2, userId),
              ]).then(
                (item) =>
                  [
                    item[0][0].concat(item[1][0]),
                    item[0][1] || item[1][1],
                  ] as const
              )
            }
            const res = await _getAllRoleList()
            const list = res[0]
            dispatch({
              type: 'setRoleList',
              data: {
                roleList: list,
              },
            })
            if (res[1]) {
              return res[1]
            }
            if (!list.length) {
              return new Error($t('请更换账号登录'))
            }
            return list
          },
          isOpenRole: async () => {
            let res = true
            try {
              res = await window.checkToken$.then(async () => {
                const bindedPlayer = await window.getBindedPlayer$
                return !bindedPlayer.isUserBind
              })
            } catch (_) {
              res = true
            }
            console.log('是否打开角色选择 =>', res)
            return res
          },
          beforeBindRole: (role) =>
            new Promise<boolean>((resolve) => {
              const _handle: Handler<SelectRoleReturnEvent> = (res) => {
                console.log('选择角色返回 =>', res)
                emitter.off('selectRoleReturn', _handle)
                resolve(res)
              }
              emitter.on('selectRoleReturn', _handle)
              emitter.emit('selectRole', { role })
            }),
          bindRole: (role) => {
            console.log('已绑定角色 =>', role)
            return Promise.resolve(role)
          },
        })
      }

      setReady(true)

      if (local) {
        document.title = local[config.title]
      }

      const appShellPending = document.querySelector('.app-shell-pending')
      if (appShellPending) {
        appShellPending.remove()
      }
    }

    return _cancel
  }, [userId, lang, local])

  // 退出登录
  useEffect(() => {
    const _handler: Handler<LogoutEvent> = (e) => {
      const { alert, rejectClose, go2login } = e

      let text = ''
      if (typeof alert === 'string') {
        text = alert
      }

      const _clearData = () => {
        clearToken()
        dispatch({
          type: 'logout',
        })
        dispatch({
          type: 'closeModal',
        })
        toast($t('退出成功'), true)
        if (go2login && !window.inSDK) {
          window.loginApp?.open()
        }
      }

      if (alert) {
        return setTimeout(() => {
          dispatch({
            type: 'setAlertData',
            data: {
              open: true,
              rejectText: $t('取消'),
              content: text,
              rejectClose,
              onResolve: () => {
                _clearData()
              },
              onReject: () => {
                if (!rejectClose) return

                _clearData()
              },
            },
          })
        }, 1)
      }

      _clearData()
    }

    emitter.on('logout', _handler)

    return () => {
      emitter.off('logout', _handler)
    }
  }, [])

  // loading
  useEffect(() => {
    const _open = () => {
      dispatch({
        type: 'setPendingData',
        data: {
          open: true,
        },
      })
    }

    const _close = () => {
      dispatch({
        type: 'setPendingData',
        data: {
          open: false,
        },
      })
    }

    emitter.on('loading', _open)
    emitter.on('closeLoading', _close)

    const _cancel = () => {
      emitter.off('loading', _open)
      emitter.off('closeLoading', _close)
    }

    return _cancel
  }, [])

  return ready ? (
    <LocalContext.Provider value={local as NonNullable<typeof local>}>
      <StateContext.Provider value={state}>
        <DispatchContext.Provider value={dispatch}>
          <Home />
          <Pending />
          <Toast />
          <SelectRole />
          <Alert />
        </DispatchContext.Provider>
      </StateContext.Provider>
    </LocalContext.Provider>
  ) : null
}

export default function App() {
  return (
    <StrictMode>
      <Root />
    </StrictMode>
  )
}
